import React from "react";
import {
  getAthleteName, getAthleteInstitution, getAthleteInstitutionLogo,
  getBracketStatusProps, getRoundLabel, groupBracketsByRound, getRankMedal, getVictoryTypeProps,
} from "../categorias.utils";
import InstitutionLogo from "../InstitutionLogo";
import WrestlingRankingTable from "../WrestlingRankingTable";

const MEDAL_STYLE = {
  1: { bg: "bg-amber-400/5",  border: "border-l-amber-300/60",  text: "text-amber-700"  },
  2: { bg: "bg-slate-400/5",  border: "border-l-slate-300/60",  text: "text-slate-500"  },
  3: { bg: "bg-amber-700/5",  border: "border-l-amber-600/40",  text: "text-amber-800"  },
};
const getMedalStyle = (rank) =>
  MEDAL_STYLE[rank] ?? { bg: "", border: "border-l-transparent", text: "text-gray-800" };

const MatchCard = ({ bracket }) => {
  const [p1Entry, p2Entry] = bracket.participants || [];
  const winnerRegId  = bracket.participants?.find(p => p.isWinner)?.registrationId;
  const p1RegId      = p1Entry?.athlete?.registrationId;
  const p2RegId      = p2Entry?.athlete?.registrationId;
  const s1           = p1Entry?.score;
  const s2           = p2Entry?.score;
  const statusProps  = getBracketStatusProps(bracket.status);
  const victoryProps = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

  const renderParticipant = (entry, score, isWin) => {
    const corner  = String(entry?.corner || "").toUpperCase();
    const logoUrl = getAthleteInstitutionLogo(entry?.athlete);
    const inst    = getAthleteInstitution(entry?.athlete);
    const name    = getAthleteName(entry?.athlete);

    return (
      <div className={`flex items-center justify-between px-3 py-2 transition-colors duration-150
        ${isWin ? "bg-green-500/[0.06]" : "bg-transparent"}`}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {corner && (
            <span className="px-1 py-0.5 rounded bg-gray-100 text-[0.58rem] font-bold tracking-wide text-gray-400 flex-shrink-0">
              {corner}
            </span>
          )}
          <InstitutionLogo logoUrl={logoUrl} name={inst} size={20} showTooltip={!!inst} />
          <div className="min-w-0">
            <p className={`text-[0.8rem] truncate ${isWin ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
              {name}
            </p>
            <p className="text-[0.67rem] text-gray-400 truncate">{inst}</p>
          </div>
        </div>
        <div className={`min-w-[32px] h-7 flex items-center justify-center rounded ml-2 flex-shrink-0
          ${isWin ? "bg-green-500/10" : "bg-gray-100"}`}
        >
          <span className={`text-[0.82rem] tabular-nums
            ${isWin ? "font-extrabold text-green-600" : "font-medium text-gray-500"}`}>
            {score ?? "—"}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden min-w-[220px] max-w-[300px]
                    hover:shadow-md hover:border-blue-300 transition-all duration-200">
      <div className="px-3 py-1.5 flex justify-between items-center bg-black/[0.03] border-b border-gray-200">
        <span className="text-[0.68rem] font-bold text-gray-400 tracking-wide">
          #{bracket.matchNumber}
        </span>
        <div className="flex items-center gap-1.5">
          {victoryProps && (
            <span
              className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`}
              title={victoryProps.title}
            >
              {victoryProps.label}
            </span>
          )}
          <span className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
            {statusProps.label}
          </span>
        </div>
      </div>
      {renderParticipant(p1Entry, s1, winnerRegId === p1RegId)}
      <hr className="mx-3 border-dashed border-gray-200" />
      {renderParticipant(p2Entry, s2, winnerRegId === p2RegId)}
    </div>
  );
};

const VistaLlaves = ({ brackets }) => {
  const roundGroups = groupBracketsByRound(brackets);
  const hasRounds   = roundGroups.length > 1;

  if (!hasRounds) {
    return (
      <div className="flex flex-wrap gap-4 p-1">
        {brackets.map((b) => (
          <MatchCard key={b.bracketId} bracket={b} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-1">
      {roundGroups.map(([round, roundBrackets]) => (
        <div key={round}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-0.5 h-4 rounded-full bg-blue-600 flex-shrink-0" />
            <span className="text-[0.7rem] font-bold uppercase tracking-widest text-gray-500">
              {getRoundLabel(round)}
            </span>
            <hr className="flex-1 border-gray-200" />
          </div>
          <div className="flex flex-wrap gap-4">
            {roundBrackets.map((b) => (
              <MatchCard key={b.bracketId ?? b.matchNumber} bracket={b} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const TablaPosiciones = ({ podium }) => {
  const hasStats = podium.some(
    (p) => p.matchesPlayed > 0 || p.wins > 0 || p.points > 0
  );

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-2.5 flex items-center gap-3 bg-black/[0.03] border-b border-gray-200">
        <div className="w-0.5 h-5 rounded-full bg-amber-500 flex-shrink-0" />
        <span className="text-[0.82rem] font-bold text-gray-800">Tabla de posiciones</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-black/[0.02] border-b border-gray-200">
              <th className="pl-5 pr-2 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 w-11">#</th>
              <th className="px-3 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Atleta</th>
              <th className="px-3 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 hidden sm:table-cell">Universidad</th>
              {hasStats && (
                <>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400" title="Partidos jugados">PJ</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400" title="Victorias">G</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400" title="Empates">E</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400" title="Derrotas">P</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 hidden md:table-cell" title="Goles a favor">GF</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400 hidden md:table-cell" title="Goles en contra">GC</th>
                  <th className="pr-5 pl-2 py-2.5 text-center text-[0.65rem] font-extrabold uppercase tracking-wide text-gray-400">Pts</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {podium.map((entry, idx) => {
              const rank  = entry.rank ?? idx + 1;
              const medal = getRankMedal(rank);
              const ms    = getMedalStyle(rank);
              const name  = getAthleteName(entry.athlete?.athlete);
              const inst  = getAthleteInstitution(entry.athlete?.athlete);

              return (
                <tr
                  key={entry.athlete?.registrationId ?? idx}
                  className={`border-b border-gray-100 border-l-4 hover:bg-black/[0.02] transition-colors
                    ${ms.bg} ${ms.border}`}
                >
                  <td className="pl-5 pr-2 py-2.5">
                    {medal.emoji ? (
                      <span className="text-base leading-none">{medal.emoji}</span>
                    ) : (
                      <span className="text-[0.75rem] font-bold text-gray-400 w-4 text-center block">{rank}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <InstitutionLogo
                        logoUrl={getAthleteInstitutionLogo(entry.athlete)}
                        name={inst}
                        size={22}
                        showTooltip={!!inst}
                      />
                      <span className={`text-[0.82rem] ${rank <= 3 ? `font-bold ${ms.text}` : "font-medium text-gray-800"}`}>
                        {name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 hidden sm:table-cell">
                    <span className="text-[0.72rem] text-gray-400">{inst}</span>
                  </td>
                  {hasStats && (
                    <>
                      <td className="px-2 py-2.5 text-center text-gray-500">{entry.matchesPlayed ?? 0}</td>
                      <td className={`px-2 py-2.5 text-center tabular-nums ${entry.wins > 0 ? "font-bold text-green-600" : "text-gray-500"}`}>{entry.wins ?? 0}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500">{entry.draws ?? 0}</td>
                      <td className={`px-2 py-2.5 text-center tabular-nums ${entry.losses > 0 ? "text-red-500" : "text-gray-500"}`}>{entry.losses ?? 0}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500 hidden md:table-cell">{entry.scoreFor ?? 0}</td>
                      <td className="px-2 py-2.5 text-center text-gray-500 hidden md:table-cell">{entry.scoreAgainst ?? 0}</td>
                      <td className={`pr-5 pl-2 py-2.5 text-center text-[0.9rem] font-extrabold tabular-nums
                        ${rank <= 3 ? ms.text : "text-gray-800"}`}>
                        {entry.points ?? 0}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const buildGroupRankingRows = (ranking) =>
  (ranking ?? [])
    .sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))
    .map((r) => ({
      registrationId: r.registrationId,
      place:   r.rank,
      wrestler: r.athlete?.athlete?.fullName ?? "—",
      inst:     r.athlete?.athlete?.institution?.name ?? "—",
      logoUrl:  r.athlete?.athlete?.institution?.logoUrl ?? null,
      cp:       r.CP    ?? 0,
      vt:       r.VT    ?? 0,
      st:       r.ST    ?? 0,
      tp:       r.TP    ?? 0,
      tpGvn:    r.TPGvn ?? 0,
    }));

const VistaResultados = ({ brackets, podium, ranking }) => {
  const roundGroups    = groupBracketsByRound(brackets);
  const wrestlingRows  = buildGroupRankingRows(ranking);
  const hasWrestling   = wrestlingRows.length > 0;

  return (
    <div className="flex flex-col gap-6 p-1">
      {hasWrestling
        ? <WrestlingRankingTable rows={wrestlingRows} title="Clasificación Final — Fase de Grupos" />
        : podium.length > 0 && <TablaPosiciones podium={podium} />
      }

      {roundGroups.map(([round, roundBrackets]) => (
        <div key={round} className="rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-2.5 flex items-center gap-3 bg-black/[0.03] border-b border-gray-200">
            <div className="w-0.5 h-5 rounded-full bg-blue-600 flex-shrink-0" />
            <span className="text-[0.82rem] font-bold text-gray-800">{getRoundLabel(round)}</span>
            <span className="ml-auto text-[0.6rem] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
              {roundBrackets.length} combate{roundBrackets.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" style={{ tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: "38px" }} />
                <col style={{ width: "32%" }} />
                <col style={{ width: "90px" }} />
                <col style={{ width: "70px" }} />
                <col style={{ width: "28%" }} />
                <col style={{ width: "90px" }} />
              </colgroup>
              <thead>
                <tr className="bg-black/[0.02] border-b border-gray-200">
                  <th className="pl-4 pr-2 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">#</th>
                  <th className="px-3 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Atleta A</th>
                  <th className="px-3 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Marcador</th>
                  <th className="px-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Tipo</th>
                  <th className="px-3 py-2.5 text-left text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Atleta B</th>
                  <th className="pr-4 pl-2 py-2.5 text-center text-[0.65rem] font-bold uppercase tracking-wide text-gray-400">Estado</th>
                </tr>
              </thead>
              <tbody>
                {roundBrackets.map((bracket) => {
                  const [p1Entry, p2Entry] = bracket.participants || [];
                  const winnerRegId  = bracket.participants?.find(p => p.isWinner)?.registrationId;
                  const p1RegId      = p1Entry?.athlete?.registrationId;
                  const p2RegId      = p2Entry?.athlete?.registrationId;
                  const s1           = p1Entry?.score ?? "—";
                  const s2           = p2Entry?.score ?? "—";
                  const statusProps  = getBracketStatusProps(bracket.status);
                  const hasWinner    = !!winnerRegId;
                  const victoryProps = bracket.victoryType ? getVictoryTypeProps(bracket.victoryType) : null;

                  return (
                    <tr key={bracket.bracketId}
                      className="border-b border-gray-100 hover:bg-black/[0.02] transition-colors">
                      <td className="pl-4 pr-2 py-2.5 text-[0.72rem] text-gray-400">{bracket.matchNumber}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p1Entry?.athlete)} name={getAthleteInstitution(p1Entry?.athlete)} size={18} showTooltip />
                          <div className="min-w-0">
                            <p className={`text-[0.8rem] truncate ${winnerRegId === p1RegId ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
                              {getAthleteName(p1Entry?.athlete)}
                            </p>
                            <p className="text-[0.67rem] text-gray-400 truncate">{getAthleteInstitution(p1Entry?.athlete)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg border
                          ${hasWinner ? "bg-green-500/[0.06] border-green-400/30" : "bg-gray-100 border-gray-200"}`}>
                          <span className={`text-[0.82rem] tabular-nums min-w-[18px] text-right
                            ${winnerRegId === p1RegId ? "font-extrabold text-green-600" : "font-medium text-gray-400"}`}>{s1}</span>
                          <span className="text-gray-300 text-[0.7rem] select-none">·</span>
                          <span className={`text-[0.82rem] tabular-nums min-w-[18px] text-left
                            ${winnerRegId === p2RegId ? "font-extrabold text-green-600" : "font-medium text-gray-400"}`}>{s2}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        {victoryProps ? (
                          <span className={`text-[0.6rem] font-bold px-1.5 py-0.5 rounded border ${victoryProps.color}`} title={victoryProps.title}>
                            {victoryProps.label}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <InstitutionLogo logoUrl={getAthleteInstitutionLogo(p2Entry?.athlete)} name={getAthleteInstitution(p2Entry?.athlete)} size={18} showTooltip />
                          <div className="min-w-0">
                            <p className={`text-[0.8rem] truncate ${winnerRegId === p2RegId ? "font-bold text-green-600" : "font-medium text-gray-800"}`}>
                              {getAthleteName(p2Entry?.athlete)}
                            </p>
                            <p className="text-[0.67rem] text-gray-400 truncate">{getAthleteInstitution(p2Entry?.athlete)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="pr-4 pl-2 py-2.5 text-center">
                        <span className="text-[0.62rem] font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-500 tracking-wide">
                          {statusProps.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

const FaseGrupo = ({ fase, vista }) => {
  if (!fase?.brackets?.length) {
    return (
      <div className="m-4 flex items-start gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
        <span>ℹ️</span>
        <span>No hay combates registrados en esta fase.</span>
      </div>
    );
  }

  const podium  = fase.podium  ?? [];
  const ranking = fase.ranking ?? [];

  return vista === "resultados" ? (
    <VistaResultados brackets={fase.brackets} podium={podium} ranking={ranking} />
  ) : (
    <VistaLlaves brackets={fase.brackets} />
  );
};

export default FaseGrupo;